import { Formsy, george, pr, theme, ui } from "aaa";
import { observer } from "mobx-react";
import * as React from "react";
import { pushRoute } from "stores/RouterFunctions";
import { history } from "stores/RouterStore";
import { AliasDeleteUsernameConfirmationDialog } from "./AliasDeleteUsernameConfirmationDialog";
import { AliasStore } from "./AliasStore";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { getDefaultKeyPressHandler } from "util/helpers";

const USERNAME_LENGTH_MIN = 8;
const ALLOWED_CHARACTERS = /^[a-zA-Z0-9._-]+$/;
const ONLY_DIGITS = /^\d+$/;

export const AliasManageUsername = observer(({ store }: { store: AliasStore }) => {
    const [backendError, setBackendError] = React.useState<string | undefined>(undefined);
    const [showDeleteUserNameConfirmationDialog, setShowDeleteUserNameConfirmationDialog] = React.useState(false);
    const [didAutofocus, setDidAutofocus] = React.useState(false);
    const [username, setUsername] = React.useState(store.currentUsername);

    React.useEffect(() => {
        setUsername(store.currentUsername);
    }, [store.currentUsername]);

    const handleSubmitUsername = async () => {
        try {
            await store.changeAlias(username);

            pushRoute("username-signing");
        } catch (error) {
            const errorCode = error.response?.errors?.[0]?.error;

            if (errorCode === "ALIAS_CONTAINS_SEQUENCE") {
                setBackendError(ui.__("sidentitySettings.username.validation.containsSequence"));
            } else if (errorCode === "ALIAS_ALREADY_EXISTS") {
                setBackendError(ui.__("sidentitySettings.username.validation.alreadyExists"));
            } else if (errorCode === "ALIAS_BLACKLISTED") {
                setBackendError(ui.__("sidentitySettings.username.validation.blacklisted"));
            } else if (errorCode === "FIELD_INVALID") {
                setBackendError(ui.__("sidentitySettings.username.validation.forbiddenCharacters"));
            } else if (errorCode === "FIELD_MISSING") {
                setBackendError(ui.__("sidentitySettings.username.validation.empty"));
            } else {
                pushRoute("/error");
            }
        }

    };

    const handleSubmitDeleteUserNameDialog = async (confirmed?: boolean) => {
        setShowDeleteUserNameConfirmationDialog(false);
        if (confirmed) {
            try {
                await store.deleteAlias();

                pushRoute("delete-username-signing");
            } catch (error) {
                pushRoute("/error");
            }
        }
    };

    const handleClickDeleteUserName = () => {
        setShowDeleteUserNameConfirmationDialog(true);
    };

    const handleBack = async () => {
        history.goBack();
    };

    const ref = React.useRef<HTMLAnchorElement | null>(null);

    React.useEffect(() => {
        if (ref.current && !didAutofocus) {
            ref.current.focus();
            setDidAutofocus(true);
        }
    });

    const getErrorMessage = () => {
        if (username.length === 0) {
            return ui.__("sidentitySettings.username.validation.empty");
        } else if (username.length < USERNAME_LENGTH_MIN) {
            return ui.__("sidentitySettings.username.validation.minLength", { minLength: USERNAME_LENGTH_MIN });
        } else if (!ALLOWED_CHARACTERS.test(username) || ONLY_DIGITS.test(username)) {
            return ui.__("sidentitySettings.username.validation.forbiddenCharacters");
        } else if (backendError) {
            return backendError;
        }
    };

    return (
        <>
            <george.Background>
                <george.PageWithActionButton>
                    {/* use a div with padding instead of applying it to Background component - otherwise actionbutton doesn't work on mobile */}
                    <div style={{ padding: 16 }}>
                        <a
                            ref={ref}
                            style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", cursor: "pointer" }}
                            role="button"
                            tabIndex={0}
                            onClick={handleBack}
                            onKeyPress={getDefaultKeyPressHandler(handleBack)}
                            aria-label={ui.__("a11y.backToOverview")}
                        >
                            <pr.IconChevronLeft style={{ color: "#135EE2" }} />
                            {!externalInterfaceStore.isGeorgeApp && (
                                <pr.P_16_CONTENT style={{ color: "#135EE2", fontWeight: 600 }}>
                                    {ui.__("common.backToOverview")}
                                </pr.P_16_CONTENT>
                            )}
                        </a>
                        <pr.P_32_TITLE style={{ fontWeight: 700, marginTop: 8, marginBottom: 16 }}>
                            {ui.__("sidentitySettings.navbar.username")}
                        </pr.P_32_TITLE>
                        <Formsy.Form>
                            <pr.P_14_CONTENT style={{ color: ui.isDarkMode ? "white" : "#0A285C", lineHeight: "20px", marginBottom: 36 }}>
                                {ui.__("sidentitySettings.manageUsername.text")}
                            </pr.P_14_CONTENT>
                            <george.TextFieldFormsy
                                name="sidentityusername"
                                required
                                validateEmpty
                                getErrorMessage={getErrorMessage}
                                hintText={ui.__("sidentitySettings.username.placeholder.text")}
                                onChange={(value: string) => {
                                    setBackendError(undefined);
                                    setUsername(value);
                                }}
                                inputStyle={{
                                    color: ui.isDarkMode ? "white" : "#0A285C",
                                    fontSize: 18
                                }}
                                lineHeight={32}
                                inputFrameStyle={ui.isDarkMode ? {
                                    // unset the height in order to make the lineHeight prop work
                                    height: "unset",
                                    paddingLeft: 16,
                                    paddingRight: 16,
                                } : {
                                    backgroundColor: "white",
                                    border: "none",
                                    // unset the height in order to make the lineHeight prop work
                                    height: "unset",
                                    paddingLeft: 16,
                                    paddingRight: 16,
                                }}
                                value={username}
                                aria-label={ui.__("a11y.sidentity.username.label")}
                                maxLength={80}
                            />
                            <pr.P_14_CONTENT style={{ marginTop: 16 }}>
                                {ui.__("sidentitySettings.manageUsername.hintText", { minLength: USERNAME_LENGTH_MIN })}
                            </pr.P_14_CONTENT>
                        </Formsy.Form>
                    </div>
                    <george.ActionButton
                        onClick={handleSubmitUsername}
                        primary
                        messageId={"common.save"}
                        disableButton={!!getErrorMessage() || store.currentUsername === username}
                        style={store.currentUsername ? { bottom: theme.ACTION_BUTTON_HEIGHT } : undefined}
                    />
                    {store.currentUsername && (
                        <george.SecondaryActionButton
                            onClick={handleClickDeleteUserName}
                            messageId="sidentitySettings.manageUsername.delete"
                            style={{
                                backgroundColor: "unset",
                                bottom: 0,
                                paddingTop: 20,
                                paddingBottom: 20,
                                fontSize: 16,
                                fontWeight: "bold",
                                color: theme.themeColors.ui_red,
                                width: "100%"
                            }}
                        />
                    )}
                </george.PageWithActionButton>
            </george.Background>
            <AliasDeleteUsernameConfirmationDialog
                show={showDeleteUserNameConfirmationDialog}
                onClose={handleSubmitDeleteUserNameDialog}
            />
        </>
    );
});



// WEBPACK FOOTER //
// ./src/components/alias/AliasManageUsername.tsx